/**
 * Demo data for the Gantt chart visualization.
 * Tasks are spread across a time range for visual clarity.
 * Remove once real workflow instances are executing.
 */

import {
  GqlTaskStatus,
  GqlTaskPriority,
  GqlDependencyType,
  GqlDependencyStatus,
  type TaskFieldsFragment,
} from '@/generated/graphql';

const daysAgo = (d: number) => new Date(Date.now() - d * 86400_000).toISOString();
const daysFromNow = (d: number) => new Date(Date.now() + d * 86400_000).toISOString();
const msForDays = (d: number) => String(d * 86400_000);

let seq = 0;
function makeTask(
  overrides: Partial<TaskFieldsFragment> & { name: string; status: GqlTaskStatus },
): TaskFieldsFragment {
  seq++;
  return {
    __typename: 'Task',
    id: `gantt-demo-${seq}`,
    parentId: null,
    tenantId: 'ferp-system',
    workflowInstanceId: 'demo-rcm-workflow-001',
    description: '',
    taskType: 'MANUAL',
    assignedActor: null,
    assignedAt: null,
    priority: GqlTaskPriority.NORMAL,
    startedAt: null,
    completedAt: null,
    createdAt: daysAgo(14),
    updatedAt: daysAgo(0),
    version: 1,
    createdBy: '2_8',
    metadata: {},
    sequenceOrder: null,
    dependencies: [],
    dependents: [],
    scheduledStart: null,
    scheduledEnd: null,
    dueAt: null,
    slaId: null,
    estimatedDurationMs: null,
    durationMs: null,
    progressPercent: null,
    queueId: null,
    constraintIds: [],
    validationIds: [],
    errorMessage: null,
    retryCount: 0,
    maxRetries: 3,
    failureStrategy: 'RETRY',
    ...overrides,
  } as TaskFieldsFragment;
}

export const GANTT_DEMO_TASKS: TaskFieldsFragment[] = [
  // Phase 1: Intake (completed, days ago)
  makeTask({
    name: 'Patient Registration',
    status: GqlTaskStatus.COMPLETED,
    priority: GqlTaskPriority.HIGH,
    scheduledStart: daysAgo(13),
    scheduledEnd: daysAgo(11),
    dueAt: daysAgo(11),
    startedAt: daysAgo(12),
    completedAt: daysAgo(11),
    estimatedDurationMs: msForDays(1),
    progressPercent: 100,
  }),
  makeTask({
    name: 'Insurance Verification',
    status: GqlTaskStatus.COMPLETED,
    priority: GqlTaskPriority.CRITICAL,
    scheduledStart: daysAgo(11),
    scheduledEnd: daysAgo(9),
    dueAt: daysAgo(9),
    startedAt: daysAgo(11),
    completedAt: daysAgo(9),
    estimatedDurationMs: msForDays(2),
    progressPercent: 100,
  }),
  makeTask({
    name: 'Prior Authorization',
    status: GqlTaskStatus.COMPLETED,
    priority: GqlTaskPriority.HIGH,
    scheduledStart: daysAgo(10),
    scheduledEnd: daysAgo(7),
    dueAt: daysAgo(7),
    startedAt: daysAgo(9),
    completedAt: daysAgo(7),
    estimatedDurationMs: msForDays(3),
    progressPercent: 100,
  }),

  // Phase 2: Coding (in progress)
  makeTask({
    name: 'Clinical Documentation Review',
    status: GqlTaskStatus.COMPLETED,
    priority: GqlTaskPriority.NORMAL,
    scheduledStart: daysAgo(7),
    scheduledEnd: daysAgo(5),
    startedAt: daysAgo(7),
    completedAt: daysAgo(5),
    estimatedDurationMs: msForDays(2),
    progressPercent: 100,
  }),
  makeTask({
    name: 'ICD-10 Diagnosis Coding',
    status: GqlTaskStatus.IN_PROGRESS,
    priority: GqlTaskPriority.CRITICAL,
    assignedActor: { id: 'dr-sarah-chen', displayName: 'Dr. Sarah Chen' },
    scheduledStart: daysAgo(5),
    scheduledEnd: daysAgo(2),
    dueAt: daysAgo(1),
    startedAt: daysAgo(5),
    estimatedDurationMs: msForDays(3),
    progressPercent: 65,
  }),
  makeTask({
    name: 'CPT Procedure Coding',
    status: GqlTaskStatus.IN_PROGRESS,
    priority: GqlTaskPriority.HIGH,
    assignedActor: { id: 'j-martinez', displayName: 'J. Martinez' },
    scheduledStart: daysAgo(4),
    scheduledEnd: daysAgo(1),
    dueAt: daysAgo(1),
    startedAt: daysAgo(4),
    estimatedDurationMs: msForDays(3),
    progressPercent: 40,
  }),

  // Phase 3: Claim (upcoming)
  makeTask({
    name: 'Claim Scrubbing',
    status: GqlTaskStatus.READY,
    priority: GqlTaskPriority.NORMAL,
    scheduledStart: daysFromNow(1),
    scheduledEnd: daysFromNow(3),
    createdAt: daysAgo(3),
    estimatedDurationMs: msForDays(2),
  }),
  makeTask({
    name: 'Claim Submission (837P)',
    status: GqlTaskStatus.PENDING,
    priority: GqlTaskPriority.HIGH,
    scheduledStart: daysFromNow(3),
    scheduledEnd: daysFromNow(4),
    dueAt: daysFromNow(4),
    createdAt: daysAgo(2),
    estimatedDurationMs: msForDays(1),
  }),

  // Phase 4: Follow-up (future)
  makeTask({
    name: 'Payer Response Tracking',
    status: GqlTaskStatus.PENDING,
    priority: GqlTaskPriority.NORMAL,
    scheduledStart: daysFromNow(5),
    scheduledEnd: daysFromNow(10),
    createdAt: daysAgo(1),
    estimatedDurationMs: msForDays(5),
  }),
  makeTask({
    name: 'Denial Management',
    status: GqlTaskStatus.PENDING,
    priority: GqlTaskPriority.CRITICAL,
    scheduledStart: daysFromNow(10),
    scheduledEnd: daysFromNow(14),
    dueAt: daysFromNow(12),
    createdAt: daysAgo(1),
    estimatedDurationMs: msForDays(4),
  }),
  makeTask({
    name: 'Patient Billing',
    status: GqlTaskStatus.PENDING,
    priority: GqlTaskPriority.LOW,
    scheduledStart: daysFromNow(14),
    scheduledEnd: daysFromNow(16),
    createdAt: daysAgo(0),
    estimatedDurationMs: msForDays(2),
  }),

  // Blocked task
  makeTask({
    name: 'Compliance Audit',
    status: GqlTaskStatus.BLOCKED,
    priority: GqlTaskPriority.CRITICAL,
    scheduledStart: daysAgo(3),
    scheduledEnd: daysFromNow(2),
    dueAt: daysFromNow(1),
    startedAt: daysAgo(3),
    estimatedDurationMs: msForDays(5),
    progressPercent: 20,
    errorMessage: 'Awaiting CMS guidelines update',
  }),
];

// predecessorId/successorId are not in DependencyFieldsFragment (DRR-124 internal fields)
// but are retained in demo data for chart arrow rendering via runtime cast in GanttChart.tsx.
export const GANTT_DEMO_DEPENDENCIES = [
  {
    __typename: 'Dependency' as const,
    id: 'gantt-dep-1',
    name: 'Registration → Verification',
    predecessorId: 'gantt-demo-1',
    successorId: 'gantt-demo-2',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.SATISFIED,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-2',
    name: 'Verification → Prior Auth',
    predecessorId: 'gantt-demo-2',
    successorId: 'gantt-demo-3',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.SATISFIED,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-3',
    name: 'Prior Auth → Doc Review',
    predecessorId: 'gantt-demo-3',
    successorId: 'gantt-demo-4',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.SATISFIED,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-4',
    name: 'Doc Review → ICD-10 Coding',
    predecessorId: 'gantt-demo-4',
    successorId: 'gantt-demo-5',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.SATISFIED,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-5',
    name: 'Doc Review → CPT Coding',
    predecessorId: 'gantt-demo-4',
    successorId: 'gantt-demo-6',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.SATISFIED,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-6',
    name: 'ICD-10 → Scrubbing',
    predecessorId: 'gantt-demo-5',
    successorId: 'gantt-demo-7',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.PENDING,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-7',
    name: 'CPT → Scrubbing',
    predecessorId: 'gantt-demo-6',
    successorId: 'gantt-demo-7',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.PENDING,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-8',
    name: 'Scrubbing → Submission',
    predecessorId: 'gantt-demo-7',
    successorId: 'gantt-demo-8',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.PENDING,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-9',
    name: 'Submission → Payer Tracking',
    predecessorId: 'gantt-demo-8',
    successorId: 'gantt-demo-9',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.PENDING,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-10',
    name: 'Payer Tracking → Denial Mgmt',
    predecessorId: 'gantt-demo-9',
    successorId: 'gantt-demo-10',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: true,
    status: GqlDependencyStatus.PENDING,
    active: true,
    version: 1,
  },
  {
    __typename: 'Dependency',
    id: 'gantt-dep-11',
    name: 'Payer Tracking → Patient Billing',
    predecessorId: 'gantt-demo-9',
    successorId: 'gantt-demo-11',
    dependencyType: GqlDependencyType.FINISH_TO_START,
    isConditional: false,
    status: GqlDependencyStatus.PENDING,
    active: true,
    version: 1,
  },
];
